import { useEffect } from "react"
import { useRouter } from "next/router"

import { useOrganization } from "@clerk/nextjs"
import { Skeleton } from "@coval/ui"

import { useQueryParams } from "@/hooks/useQueryParams"
import { useRestrictedAccess } from "@/hooks/useRestrictedAccess"

interface RestrictedAccessGuardProps {
  children: React.ReactNode
  requiredPage?: string
}

export function RestrictedAccessGuard({
  children,
  requiredPage,
}: RestrictedAccessGuardProps) {
  const { isRestricted, allowedBasePaths, canAccess } = useRestrictedAccess()
  const { organization } = useOrganization()
  const router = useRouter()
  const queryParams = useQueryParams()

  useEffect(() => {
    if (isRestricted && requiredPage && !canAccess(requiredPage)) {
      const orgSlug = organization?.slug

      const firstAllowedPage = allowedBasePaths[0]
      if (firstAllowedPage && orgSlug) {
        void router.replace(queryParams.getHref(firstAllowedPage, orgSlug))
      } else if (firstAllowedPage) {
        void router.replace(firstAllowedPage)
      } else {
        void router.replace(
          orgSlug ? queryParams.getHref("/uploaded", orgSlug) : "/uploaded",
        )
      }
    }
  }, [
    isRestricted,
    requiredPage,
    canAccess,
    allowedBasePaths,
    router,
    organization?.slug,
    queryParams,
  ])

  // Show loading while redirecting
  if (isRestricted && requiredPage && !canAccess(requiredPage)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-4 w-32 rounded-md" />
        </div>
      </div>
    )
  }

  return <>{children}</>
}
